import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PurlyService } from '@jeppesen-foreflight/jmkw-core';
import {
  BasicButtonComponent,
  ToolkitApiService
} from '@jeppesen-foreflight/jmkw-toolkit';
import {
  ButtonInstance,
  ButtonParams,
  JepVectorTileLayer,
  Plugin,
  PluginService,
  ThemeColor
} from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { Observable, map, skip, Subscription, take } from 'rxjs';
import { PluginIDs } from '../../../../plugin-ids.type';
import { getPurlyUrlForLayer } from '../../utilities/purly-url-mapping.util';
import { bindPurlyVectorLayer, PurlyLayerBinding } from '../../utilities/purly-binding.util';
import { CulturalLayer } from './cultural.layer';

const id: PluginIDs = 'cultural';

export const CULTURAL_BUTTON: ButtonParams = {
  id,
  iconName: 'icon-cultural',
  buttonComponent: BasicButtonComponent,
  automationKey: 'cultural-toggle-button',
  tooltipText: 'Cultural Features',
  stateFn: (button, layer) => {
    button.active = layer.visible;
  },
  submit: (button: ButtonInstance) => {
    const layer = button.toolkitApiService.getLayerById<CulturalLayer>(
      button.id
    );
    layer?.jepTools.toggleVisibility();
  }
};

@Injectable({ providedIn: 'root' })
export class CulturalPluginService implements PluginService {
  private readonly api = inject(ToolkitApiService);
  private readonly purlyService = inject(PurlyService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly logger = inject(NGXLogger);
  private readonly subscriptions = new Subscription();

  readonly id: PluginIDs = id;

  private _layerBinding: PurlyLayerBinding<CulturalLayer> | undefined;

  private get culturalLayer(): CulturalLayer | undefined {
    return this._layerBinding?.layer;
  }

  create(): Observable<Plugin> {
    return this.purlyService.initializationResult$.pipe(
      take(1),
      map((result) => {
        // Setup binding utility for automatic URL refresh
        this._layerBinding = bindPurlyVectorLayer({
          purly$: this.purlyService.initializationResult$.pipe(skip(1)),
          toolkitApi: this.api,
          makeLayer: (url: string) =>
            new CulturalLayer({
              id,
              zOrder: 1,
              visible: true,
              label: 'Cultural Features',
              url
            }),
          extractUrl: (result) => {
            const tileServiceUrls = this.purlyService.getTileServiceUrls();
            return getPurlyUrlForLayer('cultural', tileServiceUrls);
          },
          pluginIds: { parent: 'aviation', child: id },
          logger: this.logger,
          initialResult: result
        });

        const layer = this._layerBinding.layer;

        if (!layer) {
          this.logger.warn(
            'Cultural plugin: No layer created from binding utility'
          );
        }

        // Setup theme switching after layer is created
        this.setupBasemapSubscription();

        return new Plugin({
          id,
          layer: layer as CulturalLayer,
          button: CULTURAL_BUTTON
        });
      })
    );
  }

  /**
   * Get current cultural layer
   */
  getLayer(): CulturalLayer | undefined {
    return this._layerBinding?.layer;
  }

  private setupBasemapSubscription(): void {
    const layer = this.culturalLayer;

    if (layer?.currentStyleInfo != undefined) {
      // Subscribe to theme changes to switch between Light/* and Dark/* style layers
      const sub = this.api
        .currentBasemap$()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((basemap: { theme: ThemeColor }) => {
          this.handleThemedRegex(basemap.theme);
        });

      this.subscriptions.add(sub);
    } else {
      // Layer style info not loaded yet, retry
      setTimeout(() => {
        this.setupBasemapSubscription();
      }, 100);
    }
  }

  private handleThemedRegex(theme: ThemeColor) {
    const layer = this.culturalLayer;

    if (!layer) {
      return theme;
    }

    // Delegate to layer's applyTheme method which calls updateVectorStyles
    layer.applyTheme(theme);

    return theme;
  }
}
